import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Tooltip,
  Grid,
  CircularProgress,
  Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ViewRoles = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const [rowsPerPage] = useState(7);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Get current page from URL params
  const page = parseInt(searchParams.get('page')) || 1;
  const search = searchParams.get('search') || '';

  useEffect(() => {
    fetchRoles();
  }, [page, search]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError(null);
  
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
  
      const token = localStorage.getItem("token");
  
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
  
      // FIXED URL
      let url = `${API_URL}/api/roles?page=${page}&limit=${rowsPerPage}`;
  
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
  
      const response = await axios.get(url, {
        signal: controller.signal,
        validateStatus: status => status < 500,
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
  
      clearTimeout(timeoutId);
  
      if (response.data.success) {
        setRoles(response.data.data);
        setTotalCount(response.data.total);
        setTotalPages(Math.ceil(response.data.total / rowsPerPage));
      } else {
        //console.error('Error response:', response.data);
        setError(
          'Failed to load roles data: ' +
          (response.data.message || 'Unknown error')
        );
      }
    } catch (error) {
      //console.error('Error fetching roles data:', error);
      setError(
        'Failed to connect to the server. Please check if the backend service is running.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setSearchParams({ page: '1', search: term });
  };

  const handleEdit = (employeeId) => {
    const roleToEdit = roles.find(role => role.employee_id === employeeId);
    navigate('/hr-update-role', {
      state: {
        employee_id: employeeId,
        isEdit: true,
        roleData: roleToEdit
      }
    });
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem("token"); // get JWT token
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
    
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await axios.delete(`${API_URL}/api/roles/${deleteId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,"content-type": "application/json"
          }
        }
      );
      if (response.data.success) {
        alert('Role deleted successfully');
        fetchRoles();
      } else {
        alert('Failed to delete role: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      //console.error('Error deleting role:', error);
      alert('Failed to delete role: ' + (error.message || 'Unknown error'));
    } finally {
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleView = (role) => {
    setSelectedRole(role);
    setViewDialogOpen(true);
  };

  const handleAddNew = () => {
    navigate('/addroles', { state: { isEdit: false } });
  };

  const retryFetch = () => {
    fetchRoles();
  };

  const handlePageChange = (event, newPage) => {
    setSearchParams({ page: newPage.toString(), search });
  };

  const getRoleChipColor = (roleType) => {
    switch (roleType) {
      case 'CEO':
        return 'error';
      case 'Manager':
        return 'warning';
      case 'Team Lead':
        return 'info';
      case 'Employee':
        return 'success';
      case 'Intern':
        return 'default';
      default:
        return 'primary';
    }
  };

  const getAccessLevelChipColor = (level) => {
    switch (level) {
      case 'Admin':
        return 'error';
      case 'High':
        return 'warning';
      case 'Medium':
        return 'info';
      case 'Low':
        return 'success';
      default:
        return 'default';
    }
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <Box sx={{ maxWidth: "1500px", margin: 'auto', padding: 3, position: 'relative' }}>
      <Typography variant="h4" gutterBottom  fontWeight="bold">
       View Roles
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, }}>
        <TextField
        sx={{ width: '300px' }}
          placeholder="Search"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: '#fff8f8', borderLeft: '4px solid #f44336' }}>
          <Typography color="error" gutterBottom><strong>Error:</strong> {error}</Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={retryFetch}
            startIcon={<SearchIcon />}
          >
            Retry
          </Button>
        </Paper>
      )}

      <div style={{ position: 'relative' }}>
        <TableContainer component={Paper} elevation={3} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Employee ID</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Role</strong></TableCell>
                <TableCell><strong>Department</strong></TableCell>
                <TableCell><strong>Reporting To</strong></TableCell>
                <TableCell><strong>Responsibilities</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No roles found</TableCell>
                </TableRow>
              ) : (
                roles.map((role) => (
                  <TableRow key={role.employee_id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: 'underline',
                          color: 'primary.main',
                          cursor: 'pointer',
                          '&:hover': {
                            color: 'primary.dark',
                            fontWeight: 'bold'
                          }
                        }}
                        onClick={() => handleEdit(role.employee_id)}
                      >
                        {role.employee_id}
                      </Typography>
                    </TableCell>
                    <TableCell>{role.fullName}</TableCell>
                    <TableCell>
                      {role.roleType}
                    </TableCell>
                    <TableCell>{role.department}</TableCell>
                    <TableCell>{role.reportingManager}</TableCell>
                    <TableCell>
                      {role.selectedResponsibilities ? (
                        <Tooltip title={role.selectedResponsibilities.join(', ')}>
                          <Typography variant="body2">
                            {truncateText(role.selectedResponsibilities.join(', '))}
                          </Typography>
                        </Tooltip>
                      ) : "N/A"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            disabled={loading || totalCount === 0}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {(page - 1) * rowsPerPage + 1} - {Math.min(page * rowsPerPage, totalCount)} of {totalCount} roles
          </Typography>
        </Box>
      </div>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this role? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {selectedRole && (
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Role Details - {selectedRole.fullName}
            <Typography variant="subtitle2" color="text.secondary">
              {selectedRole.employee_id}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>Employee Information</Typography>
                  <Typography><strong>Name:</strong> {selectedRole.fullName}</Typography>
                  <Typography><strong>Email:</strong> {selectedRole.email}</Typography>
                  <Typography><strong>Department:</strong> {selectedRole.department}</Typography>
                  <Typography><strong>Designation:</strong> {selectedRole.designation}</Typography>
                  <Typography><strong>Joining Date:</strong> {selectedRole.joiningDate}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>Role Information</Typography>
                  <Typography>
                    <strong>Role Type:</strong>
                    <Chip
                      label={selectedRole.roleType}
                      color={getRoleChipColor(selectedRole.roleType)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Typography><strong>Reporting Manager:</strong> {selectedRole.reportingManager}</Typography>
                  <Typography><strong>Team Size:</strong> {selectedRole.teamSize}</Typography>
                  <Typography>
                    <strong>Access Level:</strong>
                    <Chip
                      label={selectedRole.accessLevel}
                      color={getAccessLevelChipColor(selectedRole.accessLevel)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Responsibilities</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {selectedRole.selectedResponsibilities && selectedRole.selectedResponsibilities.map((resp, index) => (
                      <Chip key={index} label={resp} color="primary" variant="outlined" />
                    ))}
                  </Box>
                  {selectedRole.additionalResponsibilities && (
                    <>
                      <Typography variant="subtitle1"><strong>Additional Responsibilities:</strong></Typography>
                      <Typography paragraph>{selectedRole.additionalResponsibilities}</Typography>
                    </>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
                  <Typography paragraph><strong>KPIs:</strong> {selectedRole.kpis}</Typography>
                  <Typography paragraph><strong>Evaluation Criteria:</strong> {selectedRole.evaluationCriteria}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography><strong>Last Promotion:</strong> {selectedRole.lastPromotionDate}</Typography>
                    <Typography><strong>Next Review:</strong> {selectedRole.nextReviewDate}</Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setViewDialogOpen(false);
                handleEdit(selectedRole.employee_id);
              }}
              color="primary"
              variant="contained"
            >
              Edit Role
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ViewRoles;